import { Alert, Box, Flex, Paper, Progress, Text } from "@mantine/core";
import { ShieldCheckIcon, ShieldSlashIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { currencyFormat } from "../../utils";

interface Props {
  creditHold: {
    onCreditHold: boolean;
    creditHoldReason: string | null;
    creditLimit: number | null;
    availableCredit: number | null;
  } | null;
}

/**
 * Credit control for one counterparty, read straight from the AR account.
 *
 * Read-only by design: nothing in the platform sets `creditLimit` yet, so the
 * card renders nothing at all rather than showing a fabricated ₮0 limit. It
 * appears as soon as a limit exists on the account.
 */
export function CreditControlCard({ creditHold }: Props) {
  const { t } = useTranslation("billing");

  if (!creditHold || creditHold.creditLimit == null) return null;

  const limit = Number(creditHold.creditLimit) || 0;
  const available = Number(creditHold.availableCredit ?? 0);
  const used = Math.max(limit - available, 0);
  const utilisation = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

  const reasonLabels: Record<string, string> = {
    OVERDUE_BALANCE      : t("creditHold.reasonOverdue"),
    CREDIT_LIMIT_EXCEEDED: t("creditHold.reasonLimitExceeded"),
    PAYMENT_HISTORY      : t("creditHold.reasonPaymentHistory"),
    DISPUTE_PENDING      : t("creditHold.reasonDisputePending"),
    MANUAL               : t("creditHold.reasonManual"),
  };

  const reason = creditHold.creditHoldReason
    ? reasonLabels[creditHold.creditHoldReason] ?? creditHold.creditHoldReason
    : t("creditHold.unknownReason");

  return (
    <Paper p="md" radius="md" withBorder>
      <Flex align="center" gap={8} mb="sm">
        {creditHold.onCreditHold
          ? <ShieldSlashIcon size={16} weight="bold" />
          : <ShieldCheckIcon size={16} weight="bold" />}
        <Text fw={700} fz="sm">{t("creditControl.title")}</Text>
      </Flex>

      <Flex justify="space-between" mb={6}>
        <Text fz="sm" c="dimmed">{t("creditControl.limit")}</Text>
        <Text fz="sm" fw={600}>{currencyFormat(limit)}</Text>
      </Flex>

      <Flex justify="space-between" mb="xs">
        <Text fz="sm" c="dimmed">{t("creditControl.available")}</Text>
        <Text fz="sm" fw={600} c={available <= 0 ? "red.8" : "gray.8"}>{currencyFormat(available)}</Text>
      </Flex>

      <Progress
        value={utilisation}
        color={utilisation >= 90 ? "red" : utilisation >= 70 ? "yellow" : "green"}
        size="sm"
        radius="xl"
        aria-label={t("creditControl.utilisation", { percent: Math.round(utilisation) })}
      />
      <Text fz="sm" c="dimmed" mt={4}>
        {t("creditControl.utilisation", { percent: Math.round(utilisation) })}
      </Text>

      <Box mt="sm">
        {creditHold.onCreditHold ? (
          <Alert color="red" radius="md" p="xs" icon={<ShieldSlashIcon size={14} weight="bold" />}>
            <Text fz="sm">{t("creditControl.onHold", { reason })}</Text>
          </Alert>
        ) : (
          <Text fz="sm" c="dimmed">{t("creditControl.notOnHold")}</Text>
        )}
      </Box>
    </Paper>
  );
}
